import { useRef, useState } from 'react';
import { computeLabor, LABOR_DEFAULTS, type LaborInputs, type LaborResults, type VarianceLabel } from '@bp/shared';
import { cn } from '@/lib/utils';
import { Badge, Card, CardHeader } from '../ui';

const FIELDS: { key: keyof LaborInputs; label: string; hint?: string; prefix?: string; placeholder?: string }[] = [
  { key: 'weekly_sales', label: 'Weekly sales', prefix: '$' },
  { key: 'target_splh', label: 'Target SPLH', hint: 'Sales per labor hour', placeholder: String(LABOR_DEFAULTS.target_splh) },
  { key: 'scheduled_hours', label: 'Weekly scheduled hours' },
  { key: 'employees', label: 'Employees on schedule' },
  { key: 'marks_formula', label: "Mark's Formula value" },
  { key: 'avg_hours_per_employee', label: 'Avg hours per employee', placeholder: String(LABOR_DEFAULTS.avg_hours_per_employee) },
];

const vTone = (l: VarianceLabel | null) => (l === 'On Target' ? 'pass' : l === 'Overscheduled' ? 'warn' : l ? 'fail' : 'neutral');

export function LaborResultsTable({ r }: { r: LaborResults }) {
  const rows: [string, React.ReactNode][] = [
    ['Correct labor hours (sales ÷ SPLH)', r.correct_hours],
    ['Break hours', r.break_hours],
    ['Actual scheduled hours (less breaks)', r.actual_hours],
    ['Variance', r.variance !== null && <span className="inline-flex items-center gap-2">{r.variance > 0 ? '+' : ''}{r.variance} h <Badge tone={vTone(r.variance_label)}>{r.variance_label}</Badge></span>],
    ['Employees needed', r.employees_needed],
    ['Employees by sales', r.employees_by_sales],
    ["Mark's hours (M + breaks)", r.mark_hours],
    ["Mark's variance", r.mark_variance !== null && <span className="inline-flex items-center gap-2">{r.mark_variance > 0 ? '+' : ''}{r.mark_variance} h <Badge tone={vTone(r.mark_variance_label)}>{r.mark_variance_label}</Badge></span>],
  ];
  if (r.correct_hours === null) return <p className="px-4 py-6 text-sm text-muted-fg">Enter weekly sales to see results.</p>;
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([label, v]) => (
          <tr key={label} className="border-b last:border-0">
            <td className="px-4 py-2 text-muted-fg">{label}</td>
            <td className="tabular px-4 py-2 text-right font-medium">{v ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function LaborPanel({ inputs, onChange, children }: { inputs: LaborInputs; onChange: (i: LaborInputs) => void; children?: React.ReactNode }) {
  const [local, setLocal] = useState<Record<string, string>>(() => Object.fromEntries(FIELDS.map((f) => [f.key, inputs[f.key]?.toString() ?? ''])));
  const timer = useRef<number | undefined>(undefined);
  const parsed: LaborInputs = Object.fromEntries(FIELDS.map((f) => [f.key, local[f.key].trim() === '' ? null : Number(local[f.key])]));
  const results = computeLabor(parsed);

  const set = (k: string, v: string) => {
    const next = { ...local, [k]: v };
    setLocal(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      onChange(Object.fromEntries(FIELDS.map((f) => [f.key, next[f.key].trim() === '' ? null : Number(next[f.key])])));
    }, 600);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card>
        <CardHeader title="Labor Planning Calculator" subtitle="Saved with this audit as you type." />
        <div className="grid gap-4 p-4 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="mb-1.5 block text-sm font-medium">{f.label}</span>
              <div className="relative">
                {f.prefix && <span className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-fg">{f.prefix}</span>}
                <input
                  type="number"
                  inputMode="decimal"
                  value={local[f.key]}
                  placeholder={f.placeholder}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={cn('h-12 w-full rounded-lg border bg-card px-3 text-lg tabular', f.prefix && 'pl-7')}
                />
              </div>
              {f.hint && <span className="mt-1 block text-xs text-muted-fg">{f.hint}</span>}
            </label>
          ))}
        </div>
      </Card>
      <div className="space-y-5">
        <Card>
          <CardHeader title="Results" />
          <LaborResultsTable r={results} />
        </Card>
        {children}
      </div>
    </div>
  );
}
